// src/Components/dashboard/UserAnalyticsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Users, UserCheck, TrendingUp, MessageSquare,
  UserPlus, Activity,
} from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import { createDashboardRealtimeClient } from '../../services/analyticsRealtime';
import { useTimeRange } from '../layout/DashboardLayout';
import {
  fmt, Skeleton, Card, PageHeader, EmptyState,
  RankBadge, RefreshBtn, ExportBtn,
} from '../dashboard/design-system';

// ── Tooltip ───────────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 border border-white/10 rounded-xl px-3 py-2.5 shadow-xl text-xs">
      <p className="text-white/50 mb-1.5 font-medium">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-white/70">{p.name}:</span>
          <span className="text-white font-semibold">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// ── Activity heatmap (real post timestamps) ───────────────────────────────────
function ActivityHeatmap({ posts }) {
  const WEEKS = 16;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const activityMap = {};
  (posts || []).forEach(p => {
    const d = new Date(p.date_created || p.created_at || p.date_published);
    if (!isNaN(d)) {
      const key = d.toISOString().split('T')[0];
      activityMap[key] = (activityMap[key] || 0) + 1;
    }
  });

  const cells = [];
  for (let w = WEEKS - 1; w >= 0; w--) {
    const week = [];
    for (let d = 6; d >= 0; d--) {
      const date = new Date(today);
      date.setDate(today.getDate() - (w * 7 + d));
      const key   = date.toISOString().split('T')[0];
      const count = activityMap[key] || 0;
      week.push({ date: key, count, label: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) });
    }
    cells.push(week);
  }

  const maxActivity = Math.max(...Object.values(activityMap), 1);
  const level = (count) => {
    if (count === 0) return 0;
    const pct = count / maxActivity;
    if (pct < 0.25) return 1;
    if (pct < 0.50) return 2;
    if (pct < 0.75) return 3;
    return 4;
  };

  const colors = [
    'bg-gray-100 dark:bg-gray-800',
    'bg-red-100 dark:bg-red-900/30',
    'bg-red-300 dark:bg-red-700/50',
    'bg-red-500',
    'bg-red-700',
  ];

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const totalActivity = Object.values(activityMap).reduce((s, v) => s + v, 0);

  return (
    <div>
      <div className="flex items-start gap-1 overflow-x-auto pb-2">
        <div className="flex flex-col gap-0.5 mr-1 flex-shrink-0 mt-5">
          {DAYS.map((d, i) => (
            <div key={d} className="h-3 w-6 text-[9px] text-gray-400 flex items-center">
              {i % 2 === 1 ? d : ''}
            </div>
          ))}
        </div>
        {cells.map((week, wi) => {
          const firstDay = new Date(week[0].date);
          const showMonth = firstDay.getDate() <= 7 || wi === 0;
          return (
            <div key={wi} className="flex flex-col gap-0.5 flex-shrink-0">
              <div className="h-4 text-[9px] text-gray-400 flex items-center">
                {showMonth ? firstDay.toLocaleDateString('en-US', { month: 'short' }) : ''}
              </div>
              {week.slice().reverse().map((cell, di) => (
                <div key={di}
                  title={`${cell.label}: ${cell.count} post${cell.count !== 1 ? 's' : ''}`}
                  className={`w-3 h-3 rounded-sm cursor-default transition-opacity hover:opacity-75 ${colors[level(cell.count)]}`}
                />
              ))}
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-gray-400">{totalActivity} posts in {WEEKS} weeks</p>
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400">Less</span>
          {colors.map((c, i) => <div key={i} className={`w-3 h-3 rounded-sm ${c}`} />)}
          <span className="text-xs text-gray-400">More</span>
        </div>
      </div>
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
const KPICard = ({ title, value, sub, icon: Icon, accent, badge, loading }) => (
  <Card className="p-5">
    <div className="flex items-start justify-between mb-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}18` }}>
        <Icon size={20} style={{ color: accent }} />
      </div>
      {badge && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
          {badge}
        </span>
      )}
    </div>
    {loading ? <Skeleton className="h-8 w-24 mb-1" /> : (
      <p className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{value}</p>
    )}
    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-0.5">{title}</p>
    <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
  </Card>
);

// ═══════════════════════════════════════════════════════════════════════════════
export default function UserAnalyticsPage() {
  const { timeRange }           = useTimeRange();
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [data,     setData]     = useState(null);
  const [rawPosts, setRawPosts] = useState([]);
  const [timelinePosts, setTimelinePosts] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [realtime, setRealtime] = useState(false);

  const applySnapshot = useCallback((snapshot) => {
    const usr = snapshot?.users     || {};
    const con = snapshot?.content   || {};

    const registeredAccounts =
      usr.registeredAccounts ??
      usr.totalUsers ??
      0;

    const profiledUsers =
      usr.profiledUsers ??
      usr.totalProfiledUsers ??
      registeredAccounts;

    setData({
      totalUsers:      registeredAccounts,
      registeredAccounts,
      profiledUsers,
      activeUsers:     usr.activeUsers   ?? 0,
      inactiveUsers:   usr.inactiveUsers ?? 0,
      retentionRate:   usr.retentionRate ?? null,
      returningContributors: usr.returningContributors ?? 0,
      activityRate:    usr.activityRate  ?? 0,
      userGrowth:      usr.userGrowth    || usr.growthOverTime || [],
      topContributors: snapshot?.topCreators || usr.topContributors || [],
      // Traction metrics on this page
      profilesCreated: profiledUsers,
      messagesSent:    0, // needs directus_messages table
    });

    setRawPosts(con.topPosts || []);
    setLastUpdated(new Date());
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [snapRes, msgRes, timelineRes] = await Promise.allSettled([
        dashboardAPI.getDashboardSnapshot(timeRange, 50),
        dashboardAPI.getMessageAnalytics(timeRange),
        dashboardAPI.getPostsTimeline(timeRange),
      ]);
      if (snapRes.status === 'fulfilled' && snapRes.value?.data) {
        applySnapshot(snapRes.value.data);
      }
      if (msgRes.status === 'fulfilled' && msgRes.value?.data) {
        const m = msgRes.value.data;
        setData(prev => prev ? { ...prev, messagesSent: m.totalMessages ?? m.messagesSent ?? 0 } : prev);
      }
      if (timelineRes.status === 'fulfilled' && timelineRes.value?.data) {
        setTimelinePosts(timelineRes.value.data.posts || timelineRes.value.data || []);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [applySnapshot, timeRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const c = createDashboardRealtimeClient({
      range: timeRange,
      onSnapshot: p => { if (p?.data) { applySnapshot(p.data); setRealtime(true); } },
      onUpdate:   p => { if (p?.data) applySnapshot(p.data); },
      onError:    () => setRealtime(false),
    });
    c.requestSnapshot();
    return () => c.disconnect();
  }, [applySnapshot, timeRange]);

  const exportCSV = () => {
    if (!data) return;
    const csv = [
      ['Beetroot – User Analytics', new Date().toLocaleString(), timeRange],
      [],
      ['Metric', 'Value'],
      ['Total Users',      data.totalUsers],
      ['Active Users',     data.activeUsers],
      ['Inactive Users',   data.inactiveUsers],
      ['Messages Sent',    data.messagesSent],
      [],
      ['Author', 'Posts', 'Engagement'],
      ...(data.topContributors || []).map(c => [
        c.displayName || c.authorId, c.posts || c.postCount, c.engagement,
      ]),
    ].map(r => r.join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `users-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`,
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Build user growth bar chart data
  const growthChartData = (data?.userGrowth || []).map(t => ({
    label:       t.month || t.label || '',
    newUsers:    t.newUsers    || 0,
    activeUsers: t.activeUsers || t.contributors || 0,
    cumulative:  t.cumulativeUsers || 0,
  }));

  const retention = data?.retentionRate
    ?? (data?.topContributors?.length > 0
      ? Math.round((data.topContributors.filter(c => (c.posts || c.postCount || 0) >= 2).length / data.topContributors.length) * 100)
      : null);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-6 space-y-6">

      <PageHeader title="User Analytics" sub={`Activity & growth · ${timeRange}`}
        icon={Users} lastUpdated={lastUpdated} realtime={realtime}>
        <RefreshBtn onClick={fetchData} loading={loading} />
        <ExportBtn onClick={exportCSV} />
      </PageHeader>

      {error && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 rounded-xl px-4 py-3 text-sm">
          {error} — <button onClick={fetchData} className="underline font-medium">retry</button>
        </div>
      )}

      {/* ── KPIs row 1 — core user metrics ───────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Registered Accounts" value={fmt(data?.registeredAccounts)} sub="Auth users" icon={Users} accent="#0EA5E9" loading={loading} />
        <KPICard title="Profiled Users" value={fmt(data?.profiledUsers)} sub="Users with profile records" icon={UserPlus} accent="#0284C7" loading={loading} />
        <KPICard title="Active Users"     value={fmt(data?.activeUsers)}     sub={`Last ${timeRange}`}     icon={UserCheck}    accent="#10B981" badge="Live" loading={loading} />
        <KPICard title="Retention Rate"   value={loading ? '—' : retention != null ? `${retention}%` : 'N/A'} sub="Returning contributors" icon={TrendingUp} accent="#7C3AED" loading={loading} />
      </div>

      {/* ── KPIs row 2 — traction metrics ────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Messages Sent"  value={data?.messagesSent > 0 ? fmt(data.messagesSent) : '—'} sub="Direct messages" icon={MessageSquare} accent="#C8102E" loading={loading} />
        <KPICard title="Activity Rate"  value={loading ? '—' : data?.activityRate > 0 ? `${data.activityRate}%` : data?.registeredAccounts > 0 ? `${Math.round((data.activeUsers / data.registeredAccounts) * 100)}%` : '—'} sub="Active / registered" icon={Activity} accent="#0EA5E9" loading={loading} />
        <KPICard title="Top Contributor Posts" value={fmt(data?.topContributors?.[0]?.posts ?? data?.topContributors?.[0]?.postCount)} sub="Most active creator" icon={Users} accent="#10B981" loading={loading} />
        <KPICard title="Returning Contributors" value={fmt(data?.returningContributors)} sub="Posted in multiple periods" icon={UserCheck} accent="#7C3AED" loading={loading} />
      </div>

      {/* ── Growth chart + quick stats ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">User Growth Over Time</h3>
          <p className="text-xs text-gray-400 mb-5">New registrations per period</p>
          {loading ? <Skeleton className="h-52 w-full" /> : growthChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={210}>
              <AreaChart data={growthChartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#0EA5E9" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0}   />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="newUsers"    name="New Users"    stroke="#0EA5E9" fill="url(#gNew)" strokeWidth={2} dot={false} />
                <Area type="monotone" dataKey="activeUsers" name="Active Users" stroke="#10B981" fill="none"        strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState icon={TrendingUp} title="No growth data" sub="Try a wider time range" />
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-5">Quick Insights</h3>
          {loading ? (
            <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
          ) : (
            <div className="space-y-2">
              {[
                { label: 'Total registered',      value: fmt(data?.registeredAccounts), color: 'text-sky-600'    },
                { label: 'Profiled users',        value: fmt(data?.profiledUsers),     color: 'text-cyan-600'   },
                { label: `Active (${timeRange})`,  value: fmt(data?.activeUsers),    color: 'text-emerald-600'},
                { label: 'Activity rate',          value: data?.registeredAccounts > 0 ? `${Math.round((data.activeUsers / data.registeredAccounts) * 100)}%` : 'N/A', color: 'text-violet-600' },
                { label: 'Retention (est.)',       value: retention != null ? `${retention}%` : 'N/A', color: 'text-amber-600'  },
                { label: 'Messages sent',          value: data?.messagesSent > 0 ? fmt(data.messagesSent) : 'No data', color: 'text-red-600' },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex justify-between items-center px-3 py-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                  <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</span>
                  <span className={`text-sm font-bold ${color}`}>{value}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* ── Activity heatmap ─────────────────────────────────────────────── */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Post Activity Heatmap</h3>
        <p className="text-xs text-gray-400 mb-5">Real post creation timestamps from your database</p>
        {loading ? <Skeleton className="h-28 w-full" /> : <ActivityHeatmap posts={timelinePosts.length > 0 ? timelinePosts : rawPosts} />}
      </Card>

      {/* ── Top contributors ─────────────────────────────────────────────── */}
      <Card className="p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-5">Top Contributors</h3>
        {loading ? (
          <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : !data?.topContributors?.length ? (
          <EmptyState icon={Users} title="No contributors found" sub="Data appears once posts receive engagement" />
        ) : (
          <div className="space-y-1">
            {data.topContributors.map((c, i) => {
              const posts   = c.postCount || c.posts || 0;
              const maxPost = data.topContributors[0]?.postCount || data.topContributors[0]?.posts || 1;
              const pct     = Math.round((posts / maxPost) * 100);
              return (
                <div key={c.authorId} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  <RankBadge rank={i + 1} />
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {(c.displayName || c.authorId || '?').substring(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                      {c.displayName || `Author ${c.authorId?.substring(0, 12)}`}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 max-w-32">
                        <div className="bg-sky-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-400">{posts} posts</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-sky-600">{fmt(c.engagement)}</p>
                    <p className="text-[10px] text-gray-400">engagement</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Messages note ────────────────────────────────────────────────── */}
      {!loading && (data?.messagesSent === 0 || data?.messagesSent == null) && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
          <p className="text-sm text-blue-800 dark:text-blue-300">
            <strong>Messages Sent</strong> shows "—" because the <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded text-xs">directus_messages</code> table
            is not yet in your Supabase schema. Add it and the metric will populate automatically.
          </p>
        </div>
      )}
    </div>
  );
}